import { AlertCircle, Loader2, Wallet } from "lucide-react";
import * as React from "react";

import { usePayinFeePreview } from "@/modules/dashboard/payins/hooks/usePayinFeePreview";
import type {
  PayinRail,
  PreviewPayinResponse,
} from "@/modules/dashboard/payins/services/paymentsPayin.service";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaLabel } from "@/shared/components/forms/ColocaLabel";
import { ColocaTabs, type ColocaTabItem } from "@/shared/components/navigation/ColocaTabs";
import {
  AmountBreakdownList,
  type AmountBreakdownRow,
} from "@/shared/components/payments/AmountBreakdownList";
import { FeeBearerSwitch } from "@/shared/components/payments/FeeBearerSwitch";
import { useAvailableCorridors } from "@/shared/hooks/useAvailableCorridors";
import { useTaxLabel } from "@/shared/hooks/useTaxLabel";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import type { LimitPayerType } from "@/shared/services/payments/paymentCorridors.service";
import {
  boundsForPayer,
  formatLimitAmount,
  isAmountLimitCode,
  resolveAmountLimitViolation,
} from "@/shared/utils/amountLimits";
import {
  EMPTY_MASKED_AMOUNT,
  maskAmount,
  type MaskedAmount,
  maskForCurrency,
} from "@/shared/utils/amountMask";
import { formatCompactAmount, formatCurrencyAmount } from "@/shared/utils/currencyMinorUnits";
import { compareDecimalStrings } from "@/shared/utils/decimal";
import { splitFeeTotal } from "@/shared/utils/paymentTaxes";

/** Which side of the operation the typed amount fixes; the other one is derived by the preview. */
export type PayinAmountMode = "gross" | "net";

/**
 * Copy is passed in rather than read here: the same control serves the collector ("quiero cobrar") and the
 * payer topping up their own wallet ("voy a pagar"), and those namespaces own their own words.
 */
export interface PayinAmountWithFeeLabels {
  modeGross: string;
  modeNet: string;
  modeGrossHint: string;
  modeNetHint: string;
  /** Label above the field, per mode. */
  inputGross: string;
  inputNet: string;
  /** Sentence for the derived side. Receives already-formatted amounts plus the currency code. */
  derivedNet: (vars: { amount: string; fee: string; currency: string }) => string;
  derivedGross: (vars: { amount: string; fee: string; currency: string }) => string;
}

/** Checked: the collector covers the fee (fixed gross). Unchecked: it is added to what the payer sends. */
export interface PayinFeeSwitchLabels {
  label: string;
  onHint: string;
  offHint: string;
  input: string;
  rowPayerSends: string;
  rowFee: string;
  rowCredited: string;
  free: string;
}

export interface PayinAmountWithFeeState {
  mode: PayinAmountMode;
  /** Null while loading, on error, or before anything is typed. */
  preview: PreviewPayinResponse | null;
  /**
   * The amount currently in the field — its masked text and its parsed value together — or `null` before
   * anything usable is typed. A wizard keeps this so a step the user walks back into can be seeded with
   * `initialAmount` instead of coming up empty.
   *
   * It is a `MaskedAmount` value object rather than a string SO THAT THE WRONG STRING CANNOT BE PASSED. The
   * tempting one is right there in the same state: `preview.gross_amount`, the API's canonical decimal
   * (`"250000.00"`). Feed that back through the mask and COP reads 25.000.000 (`decimalSep` is `null`, so the
   * dot is stripped as a non-digit) while BRL and ARS read 123456 for `"1234.56"` (there the dot is the
   * THOUSANDS separator) — a silent 100× in three of the four corridors. Carrying the value instead of
   * re-deriving it from text removes the round-trip from this path altogether.
   */
  amount: MaskedAmount | null;
  loading: boolean;
  error: string | null;
}

export interface PayinAmountWithFeeProps {
  /** Without it there is nothing to price — the field renders disabled and reports no preview. */
  organizationId: string | null;
  rail: PayinRail;
  country: string;
  currency: string;
  labels: PayinAmountWithFeeLabels;
  /**
   * Replaces the mode tabs with the send panel's "who covers the cost" switch, shown only while the amount
   * carries a fee, and the derived sentence with its breakdown.
   */
  feeSwitch?: PayinFeeSwitchLabels;
  /** Raw decimal strings, e.g. `["500000", "1000000"]`. Rendered compactly (`500k`, `1M`). */
  quickAmounts?: readonly string[];
  /** `field` — labelled input sized for a form. `prominent` — the oversized figure a wizard step leads with. */
  variant?: "field" | "prominent";
  /**
   * Amount to start from — the `amount` a wizard step captured before the user moved on. Seeded once on mount
   * and re-priced immediately, so walking back into a step shows what was typed instead of an empty field
   * with a stale preview behind it. Pair with `initialMode`.
   *
   * Pass back the `PayinAmountWithFeeState.amount` you were handed, unchanged. It is a value object rather
   * than a string precisely so that a canonical decimal from elsewhere — `preview.gross_amount` — cannot be
   * put here by mistake; see that field for what the mask would make of it.
   */
  initialAmount?: MaskedAmount | null;
  /** Which side `initialAmount` fixes. Defaults to `"gross"`. */
  initialMode?: PayinAmountMode;
  /**
   * Payer kind when the host already knows it. Unknown (omitted / null) applies the widest envelope of both
   * kinds and the hint shows both caps.
   */
  payerType?: LimitPayerType | null;
  /**
   * Called on every change of the priced state; keep it referentially stable (a `useState` setter is).
   * Optional: a simulator shows the numbers and nothing downstream consumes them.
   */
  onChange?: (state: PayinAmountWithFeeState) => void;
  disabled?: boolean;
  autoFocus?: boolean;
  className?: string;
}

/**
 * One amount input whose meaning a toggle decides — what the payer sends (fixed-send) or what must land in
 * the wallet (fixed-receive) — priced live by the stateless payin preview, with the derived side shown as a
 * sentence rather than a second input.
 *
 * Shared by every corridor: Colombia (BREB) and Brazil (PIX) drive a real charge from it, while México (SPEI)
 * and Argentina (CBU/CVU) — where nothing is created and the payer just transfers to a standing account — use
 * it as a simulator. Which is why it never owns a submit button: the host decides what, if anything, happens
 * with the priced result.
 *
 * The amount is a decimal string end to end. The mask groups thousands as the user types and the preview
 * receives the raw parsed value, because the endpoint rejects any separator.
 */
export function PayinAmountWithFee({
  organizationId,
  rail,
  country,
  currency,
  labels,
  feeSwitch,
  quickAmounts,
  variant = "field",
  initialAmount = null,
  initialMode = "gross",
  payerType = null,
  onChange,
  disabled = false,
  autoFocus = false,
  className,
}: PayinAmountWithFeeProps) {
  const { activeLanguage } = useTranslations("global");
  const taxLabel = useTaxLabel();
  const { t: tPayins } = useTranslations("payins");
  const fieldId = React.useId();
  const { preview, loading, error, errorCode, request, reset } = usePayinFeePreview();

  const corridors = useAvailableCorridors(organizationId ?? undefined);
  const limits = corridors.limitsFor(rail, country, currency, "PAYIN");
  const bounds = boundsForPayer(limits, payerType);
  // Read through a ref inside the handlers: `limitsFor` builds a fresh object per render, and keying the
  // callbacks on it would re-price on every render.
  const boundsRef = React.useRef(bounds);
  boundsRef.current = bounds;
  const boundsKey = bounds ? `${bounds.min_amount ?? ""}|${bounds.max_amount ?? ""}` : "";

  const mask = React.useMemo(() => maskForCurrency(currency), [currency]);
  // Read once, by construction: this is the mount seed, and re-applying it would fight whatever the user has
  // typed since. Nothing is parsed here — the value arrives already masked, which is the whole point of the
  // prop being a value object.
  const seed = React.useRef(initialAmount ?? EMPTY_MASKED_AMOUNT).current;

  const [mode, setMode] = React.useState<PayinAmountMode>(initialMode);
  const [amountText, setAmountText] = React.useState(seed.display);
  const [amountValue, setAmountValue] = React.useState<number | null>(seed.value);
  const format = React.useCallback(
    (value: string | number | null | undefined) => formatCurrencyAmount(value, currency, activeLanguage),
    [currency, activeLanguage],
  );

  const requestPreview = React.useCallback(
    (field: PayinAmountMode, value: number | null) => {
      if (!organizationId || value == null) {
        reset();
        return;
      }
      // Out of range is settled here, without a round-trip: no preview means the host's submit stays gated
      // exactly as while one is loading. Only the gross is known before pricing, so net mode leaves it to
      // the backend, whose answer is rendered field-level below.
      if (field === "gross" && resolveAmountLimitViolation(String(value), boundsRef.current)) {
        reset();
        return;
      }
      request({ organizationId, rail, country, currency, field, amount: String(value), payerType });
    },
    [organizationId, rail, country, currency, payerType, request, reset],
  );

  const applyAmount = React.useCallback(
    (raw: string) => {
      const { display, value } = maskAmount(raw, mask);
      setAmountText(display);
      setAmountValue(value);
      requestPreview(mode, value);
    },
    [mask, mode, requestPreview],
  );

  // Switching the mode keeps the typed amount and re-prices it with its new meaning.
  const handleModeChange = React.useCallback(
    (value: string) => {
      const next = value as PayinAmountMode;
      setMode(next);
      requestPreview(next, amountValue);
    },
    [amountValue, requestPreview],
  );

  /**
   * The corridor arrives as props, and a host can change it under the same typed amount — Colombia's deposit
   * modal offers Bre-B and PSE, whose commissions differ. Without this, the figure on screen would stay the
   * previous rail's until the user retyped, i.e. the wizard would advance showing the wrong fee. Typing and
   * the mode toggle re-price through their own handlers; this covers only the corridor moving — and the
   * limits landing or the payer kind changing, which move the range the typed amount is checked against.
   */
  const corridor = `${rail}|${country}|${currency}|${payerType ?? ""}|${boundsKey}`;
  const pricedCorridor = React.useRef(corridor);
  React.useEffect(() => {
    if (pricedCorridor.current === corridor) return;
    pricedCorridor.current = corridor;
    requestPreview(mode, amountValue);
  }, [corridor, mode, amountValue, requestPreview]);

  // A seeded amount is priced once on mount: the figure and the fee sentence must agree the moment a wizard
  // step is walked back into, and the commission may well have moved since it was first quoted.
  const pendingSeed = React.useRef<number | null>(seed.value);
  React.useEffect(() => {
    const value = pendingSeed.current;
    if (value == null) return;
    pendingSeed.current = null;
    requestPreview(mode, value);
  }, [mode, requestPreview]);

  // The host mirrors this state to gate its own submit; a preview still loading or errored is no preview.
  React.useEffect(() => {
    onChange?.({
      mode,
      preview: loading || error ? null : preview,
      // Text and value together, so the seed never has to re-parse: see `PayinAmountWithFeeState.amount`.
      amount: amountValue == null ? null : { display: amountText, value: amountValue },
      loading,
      error,
    });
    // `onChange` is expected to be a stable setter — including it would loop on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, preview, amountText, amountValue, loading, error]);

  const modeTabs: ColocaTabItem[] = [
    { value: "gross", label: labels.modeGross, content: null },
    { value: "net", label: labels.modeNet, content: null },
  ];

  const inputLabel = feeSwitch ? feeSwitch.input : mode === "gross" ? labels.inputGross : labels.inputNet;
  const money = (value: string) => `${format(value)} ${currency}`;
  // While the next price loads the hook still holds the last one, so the switch and the figures stay put.
  const shown = amountValue == null ? null : preview;
  const hasFee = !!shown && compareDecimalStrings(shown.fee_total, "0") > 0;
  const split = shown ? splitFeeTotal(shown.fee_total, shown.taxes) : null;
  const feeRows = (sign: string): AmountBreakdownRow[] =>
    !split
      ? []
      : [
          ...(compareDecimalStrings(split.commission, "0") > 0
            ? [{ key: "fee", label: feeSwitch?.rowFee ?? "", value: `${sign} ${money(split.commission)}` }]
            : []),
          ...split.taxes.map((tax) => ({
            key: `tax-${tax.code}`,
            label: taxLabel(tax),
            value: `${sign} ${money(tax.amount)}`,
          })),
        ];
  const breakdownRows: AmountBreakdownRow[] =
    !feeSwitch || !shown
      ? []
      : !hasFee
        ? [
            { key: "fee", label: feeSwitch.rowFee, value: feeSwitch.free },
            { key: "credited", label: feeSwitch.rowCredited, value: money(shown.net_amount), total: true },
          ]
        : shown.direction === "FIXED_GROSS"
          ? [
              { key: "pays", label: feeSwitch.rowPayerSends, value: money(shown.gross_amount) },
              ...feeRows("−"),
              { key: "credited", label: feeSwitch.rowCredited, value: money(shown.net_amount), total: true },
            ]
          : [
              { key: "credited", label: feeSwitch.rowCredited, value: money(shown.net_amount) },
              ...feeRows("+"),
              { key: "pays", label: feeSwitch.rowPayerSends, value: money(shown.gross_amount), total: true },
            ];

  const formatLimit = (value: string | null | undefined): string =>
    value ? formatLimitAmount(value, currency, activeLanguage) : "";
  const limitViolation =
    mode === "gross" && amountValue != null ? resolveAmountLimitViolation(String(amountValue), bounds) : null;
  const limitHint = (() => {
    if (!limits) return null;
    if (payerType == null && limits.differs && limits.person.max_amount && limits.company.max_amount) {
      return tPayins("amount_limit_hint_by_payer", {
        person: formatLimit(limits.person.max_amount),
        company: formatLimit(limits.company.max_amount),
      });
    }
    if (!bounds) return null;
    if (bounds.min_amount && bounds.max_amount) {
      return tPayins("amount_limit_hint_range", {
        min: formatLimit(bounds.min_amount),
        max: formatLimit(bounds.max_amount),
      });
    }
    if (bounds.max_amount) return tPayins("amount_limit_hint_max", { max: formatLimit(bounds.max_amount) });
    if (bounds.min_amount) return tPayins("amount_limit_hint_min", { min: formatLimit(bounds.min_amount) });
    return null;
  })();

  return (
    <div className={cn("space-y-5", className)}>
      {!feeSwitch && (
        <div className="space-y-1.5">
          <ColocaTabs tabs={modeTabs} value={mode} onValueChange={handleModeChange} listClassName="w-full" />
          <p className="text-xs text-muted-foreground">
            {mode === "gross" ? labels.modeGrossHint : labels.modeNetHint}
          </p>
        </div>
      )}

      <div className="space-y-1.5">
        {variant === "prominent" ? (
          <>
            <p className="mb-2 flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
              {inputLabel}
              {loading && <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />}
            </p>
            <div className="rounded-xl border-2 border-primary bg-card px-4 py-3 shadow-[0_0_0_4px_color-mix(in_oklch,var(--primary)_12%,transparent)]">
              <div className="flex items-baseline gap-3">
                <input
                  id={`${fieldId}-amount`}
                  value={amountText}
                  onChange={(event) => applyAmount(event.target.value)}
                  inputMode="numeric"
                  placeholder="0"
                  disabled={disabled}
                  autoFocus={autoFocus}
                  aria-label={inputLabel}
                  className="w-full bg-transparent font-mono text-3xl font-bold tracking-tight text-foreground outline-none placeholder:text-muted-foreground/40"
                />
                <span className="shrink-0 font-mono text-sm font-medium tracking-wider text-muted-foreground">
                  {currency}
                </span>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <ColocaLabel htmlFor={`${fieldId}-amount`}>{inputLabel}</ColocaLabel>
              {loading && (
                <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" aria-hidden="true" />
              )}
            </div>
            <div className="relative">
              <input
                id={`${fieldId}-amount`}
                value={amountText}
                onChange={(event) => applyAmount(event.target.value)}
                inputMode="numeric"
                placeholder="0"
                disabled={disabled}
                autoFocus={autoFocus}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 pr-14 text-right font-mono text-base tabular-nums shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
              />
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center text-xs text-muted-foreground"
              >
                {currency}
              </span>
            </div>
          </>
        )}

        {/* One slot for both, with its height reserved: the notice and the hint are mutually exclusive and
            neither exists until a corridor is picked or an amount is typed, so without a floor here the
            whole form below shifted the moment either appeared — selecting PSE with no amount yet was
            enough. `min-h-5` covers one line of either type scale; a hint long enough to wrap still grows,
            which is the honest trade for not reserving two lines of blank space on every consumer. */}
        <div className="min-h-5">
          {limitViolation ? (
            <p role="alert" className="text-sm font-medium text-destructive">
              {limitViolation === "above_maximum"
                ? tPayins("amount_limit_above", { max: formatLimit(bounds?.max_amount) })
                : tPayins("amount_limit_below", { min: formatLimit(bounds?.min_amount) })}
            </p>
          ) : (
            limitHint && <p className="text-xs text-muted-foreground">{limitHint}</p>
          )}
        </div>

        {/* Quick amounts feed the same single input, whatever the mode. */}
        {!!quickAmounts?.length && (
          <div className="flex flex-wrap gap-1.5 pt-1">
            {quickAmounts.map((value) => (
              <button
                key={value}
                type="button"
                disabled={disabled}
                onClick={() => applyAmount(value)}
                className="rounded-md border border-border bg-muted px-2.5 py-1 font-mono text-[11px] font-medium text-foreground transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
              >
                {formatCompactAmount(value, activeLanguage)}
              </button>
            ))}
          </div>
        )}
      </div>

      {feeSwitch && hasFee && (
        <FeeBearerSwitch
          label={feeSwitch.label}
          hint={mode === "gross" ? feeSwitch.onHint : feeSwitch.offHint}
          checked={mode === "gross"}
          onCheckedChange={(checked) => handleModeChange(checked ? "gross" : "net")}
        />
      )}

      {error &&
        (isAmountLimitCode(errorCode) ? (
          <p role="alert" className="text-sm font-medium text-destructive">
            {error}
          </p>
        ) : (
          <ColocaAlert variant="destructive" icon={AlertCircle} text={error} />
        ))}

      {feeSwitch && breakdownRows.length > 0 && (
        <AmountBreakdownList rows={breakdownRows} loading={loading} />
      )}

      {/* The derived side is an informative sentence, never a second input: fixing what the payer sends
          tells you what lands; fixing what you want to receive tells you what they must send. */}
      {!feeSwitch && preview && !loading && (
        <div className="flex items-start gap-3 rounded-xl border border-primary/25 bg-primary/5 px-4 py-3">
          <Wallet className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
          <p className="text-sm text-foreground">
            {preview.direction === "FIXED_GROSS"
              ? labels.derivedNet({
                  amount: format(preview.net_amount),
                  fee: format(preview.fee_total),
                  currency,
                })
              : labels.derivedGross({
                  amount: format(preview.gross_amount),
                  fee: format(preview.fee_total),
                  currency,
                })}
            {split && split.taxes.length > 0 && (
              <span className="mt-1 block text-xs text-muted-foreground">
                {tPayins("fee_includes_taxes", {
                  taxes: split.taxes
                    .map((tax) => `${taxLabel(tax)}: ${format(tax.amount)} ${currency}`)
                    .join(" · "),
                })}
              </span>
            )}
          </p>
        </div>
      )}
    </div>
  );
}
